/**
 * Base API Client
 * Provides common HTTP methods and error handling for all API clients
 */

import { apiConfig } from '../config/apiConfig';
import { createGuid } from '@bizfirst/common-js';
import { buildUrl } from '../config/apiEndpoints';
import { EnvelopeStrategyRegistry, type EnvelopeStrategy } from '@atlas-forms/form-actions';
import { FlowStudioEnvelopeStrategy } from '../envelope/FlowStudioEnvelopeStrategy';

/**
 * API Request Metadata
 */
export interface RequestMetadata {
  headers?: Record<string, string>;
  requestId?: string;
  correlationId?: string;
  timestamp?: string;
  version?: string;
  additional?: Record<string, string>;
}

/**
 * API Request ID
 */
export interface ApiId {
  id?: number;
  resID?: string;
  idString?: string;
}

/**
 * API Request Wrapper (for POST/PUT requests)
 */
export interface ApiRequestWrapper<T = any> {
  metadata?: RequestMetadata;
  data?: T;
  id?: ApiId;
}

/**
 * API Response Interface
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  metadata?: {
    page?: number;
    pageSize?: number;
    totalCount?: number;
    totalPages?: number;
  };
}

/**
 * Request Options
 */
export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

/**
 * Convert camelCase string to PascalCase
 */
const toPascalCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Recursively convert object keys from camelCase to PascalCase
 */
const convertKeysToPascalCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToPascalCase(item));
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const pascalKey = toPascalCase(key);
        converted[pascalKey] = convertKeysToPascalCase(obj[key]);
      }
    }
    return converted;
  }

  return obj;
};

/**
 * Base API Client Class
 */
export class BaseApiClient {
  protected baseUrl: string;
  protected envelopeStrategy: EnvelopeStrategy;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
    // Initialize envelope strategy registry and register Flow Studio strategy
    const registry = EnvelopeStrategyRegistry.getInstance();
    registry.register(new FlowStudioEnvelopeStrategy(), { override: true });
    // Use Flow Studio strategy as default for this client
    this.envelopeStrategy = registry.resolve('flow-studio');
  }

  /**
   * Build full URL from endpoint
   */
  protected buildUrl(endpoint: string): string {
    return buildUrl(this.baseUrl, endpoint);
  }

  /**
   * Get headers with authentication
   */
  protected getHeaders(customHeaders?: Record<string, string>): HeadersInit {
    const headers: Record<string, string> = {
      ...apiConfig.headers,
      ...customHeaders,
    };

    // Add Tenant ID header for multi-tenancy support
    if (apiConfig.tenantId) {
      headers['X-Tenant-ID'] = apiConfig.tenantId.toString();
    }

    // Add authorization token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Wrap request data in API format
   * @param data - The data to wrap
   * @param id - Optional ID information
   * @param nestData - If true, nests data in a 'data' property (for Create/Update). If false, spreads properties at root (for Search/Query)
   */
  protected wrapRequest<T>(data: T, id?: ApiId, nestData: boolean = false): any {
    const metadata = {
      requestId: createGuid(),
      correlationId: createGuid(),
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    // Convert data keys to PascalCase to match C# backend conventions
    const convertedData = convertKeysToPascalCase(data);

    if (nestData) {
      // For Create/Update operations: nest data in a 'data' property
      return {
        metadata,
        data: convertedData,
        ...(id ? { id } : {}),
      };
    } else {
      // For Search/Query operations: spread properties at root level
      if (convertedData && typeof convertedData === 'object') {
        return {
          metadata,
          ...convertedData,
          ...(id ? { id } : {}),
        };
      }

      return {
        metadata,
        data: convertedData,
        ...(id ? { id } : {}),
      };
    }
  }

  /**
   * Handle API response using envelope strategy
   */
  protected async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json') || contentType?.includes('application/problem+json');

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
      let errorDetails: any = undefined;

      if (isJson) {
        try {
          const errorData = await response.json();

          // Extract errors using envelope strategy
          const errors = this.envelopeStrategy.extractErrors?.(errorData) || [];
          if (errors.length > 0) {
            const firstError = errors[0] as any;
            errorMessage = firstError.message || firstError.Message || errorMessage;
          }
          // Handle ASP.NET Core ProblemDetails format
          else if (errorData.title) {
            errorMessage = errorData.detail || errorData.title;
          }
          // Handle standard error response format
          else if (errorData.message || errorData.Message) {
            errorMessage = errorData.message || errorData.Message;
          }

          errorDetails = errorData;
        } catch {
          // Ignore JSON parsing errors, use default error message
        }
      }

      return {
        success: false,
        error: {
          code: `HTTP_${response.status}`,
          message: errorMessage,
          details: errorDetails,
        },
      };
    }

    if (isJson) {
      const responseData = await response.json();

      // Convert camelCase keys to PascalCase to match C# backend conventions
      const convertedData = convertKeysToPascalCase(responseData);

      // Use envelope strategy to extract actual data from response
      const extractedData = this.envelopeStrategy.extract(convertedData);

      // Check if response indicates failure
      if (convertedData && typeof convertedData === 'object') {
        // Check for success field (after PascalCase conversion)
        if ('Success' in convertedData && convertedData.Success === false) {
          const errors = this.envelopeStrategy.extractErrors?.(convertedData) || [];
          if (errors.length > 0) {
            const firstError = errors[0] as any;
            return {
              success: false,
              error: {
                code: firstError.Code || 'API_ERROR',
                message: firstError.Message || 'An error occurred',
                details: convertedData,
              },
            };
          }
        }
      }

      // Extract PageInfo pagination metadata from the server response
      const pageInfo = (convertedData as any)?.PageInfo;
      const metadata = pageInfo ? {
        page: pageInfo.PageNumber ?? pageInfo.Page,
        pageSize: pageInfo.PageSize,
        totalCount: pageInfo.TotalRecords ?? pageInfo.TotalCount,
        totalPages: pageInfo.TotalPages,
      } : undefined;

      return {
        success: true,
        data: extractedData as T,
        ...(metadata ? { metadata } : {}),
      };
    }

    return {
      success: true,
      data: null as any,
    };
  }

  /**
   * GET request
   */
  protected async get<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options?.timeout || apiConfig.timeout
      );

      const response = await fetch(this.buildUrl(endpoint), {
        method: 'GET',
        headers: this.getHeaders(options?.headers),
        signal: options?.signal || controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * POST request (for Create operations)
   * Nests data in a 'data' property
   */
  protected async post<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options?.timeout || apiConfig.timeout
      );

      // Wrap the body in the API format with nested data
      const wrappedBody = body ? this.wrapRequest(body, undefined, true) : undefined;

      const response = await fetch(this.buildUrl(endpoint), {
        method: 'POST',
        headers: this.getHeaders(options?.headers),
        body: wrappedBody ? JSON.stringify(wrappedBody) : undefined,
        signal: options?.signal || controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * POST request for Query/Search operations
   * Spreads properties at root level alongside metadata
   */
  protected async postQuery<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options?.timeout || apiConfig.timeout
      );

      // Wrap the body in the API format with spread properties
      const wrappedBody = body ? this.wrapRequest(body, undefined, false) : undefined;

      const response = await fetch(this.buildUrl(endpoint), {
        method: 'POST',
        headers: this.getHeaders(options?.headers),
        body: wrappedBody ? JSON.stringify(wrappedBody) : undefined,
        signal: options?.signal || controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * PUT request (for Update operations)
   * Nests data in a 'data' property
   */
  protected async put<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options?.timeout || apiConfig.timeout
      );

      // Wrap the body in the API format with nested data
      const wrappedBody = body ? this.wrapRequest(body, undefined, true) : undefined;

      const response = await fetch(this.buildUrl(endpoint), {
        method: 'PUT',
        headers: this.getHeaders(options?.headers),
        body: wrappedBody ? JSON.stringify(wrappedBody) : undefined,
        signal: options?.signal || controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * DELETE request
   * Spreads properties at root level alongside metadata
   */
  protected async delete<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options?.timeout || apiConfig.timeout
      );

      // Wrap the body in the API format with spread properties
      const wrappedBody = body ? this.wrapRequest(body, undefined, false) : undefined;

      const response = await fetch(this.buildUrl(endpoint), {
        method: 'DELETE',
        headers: this.getHeaders(options?.headers),
        body: wrappedBody ? JSON.stringify(wrappedBody) : undefined,
        signal: options?.signal || controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Handle error
   */
  protected handleError(error: any): ApiResponse {
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: {
          code: 'TIMEOUT',
          message: 'Request timed out',
        },
      };
    }

    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred',
        details: error,
      },
    };
  }
}
